import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@ais-ui/ui-core';
import { cn, Units } from '@ais-ui/utils';
import { useSaveSettings } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';
import { getLengthUnit } from '../../Units/utils';

const unitsFormSchema = z.object({
  lengthUnit: z.string(),
});

type UnitsFormValues = z.infer<typeof unitsFormSchema>;

export const UnitsForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    lengthUnit: getLengthUnit(settings),
  };

  const form = useForm<UnitsFormValues>({
    resolver: zodResolver(unitsFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: UnitsFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        length_unit: data.lengthUnit,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-general-units-settings-enable-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsGeneralSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-general-units-settings-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsGeneralFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              {messages.settingsGeneralUnitsTitle}
            </h3>
            <div className="space-y-4">
              <FormField
                key="lengthUnit"
                control={form.control}
                name="lengthUnit"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsGeneralUnitsLength}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsGeneralUnitsLengthDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          id="unit-length-dropdown-button"
                          className="w-[240px]"
                        >
                          <SelectValue placeholder="Select a unit system" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            id="Metric"
                            key={Units.METRIC}
                            value={Units.METRIC}
                          >
                            Metric (km, m, cm)
                          </SelectItem>
                          <SelectItem
                            id="Imperial"
                            key={Units.IMPERIAL}
                            value={Units.IMPERIAL}
                          >
                            Imperial (mi, ft, in)
                          </SelectItem>
                          <SelectItem
                            id="Maritime"
                            key={Units.MARITIME}
                            value={Units.MARITIME}
                          >
                            Maritime (NM, m)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-general-units-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
